import type { IntlShape } from 'react-intl';
import { StreamingOverviewResponse, StreamingSectionKey } from 'fym-shared/src/music24/streaming';
import {
  StreamingAcceleratorMonth,
  StreamingAudienceAge,
  StreamingPlaySourceMonth,
  StreamingPlaySourceRow,
  StreamingSectionSourceOption,
  StreamingUgcNumbers,
  StreamingUgcPlatform,
  StreamingUgcPoint,
} from 'fym-shared/src/music24/streamingSections';

/**
 * The pure rules of the four Streaming sections for private period numbers (design
 * streaming-sections a to e): Audience, Play sources, Short video and Accelerator.
 */

export type PeriodSectionKey = Extract<StreamingSectionKey, 'audience' | 'playSources' | 'shortVideo' | 'accelerator'>;

/** What a section shows before its own read: the plan card (e4), the empty state (e2), or the read. */
export type PeriodSectionGate = 'locked' | 'noSource' | 'open';

/**
 * The gate of a section from the overview. An older server has no key for the new sections: then no
 * source gives them.
 */
export function periodSectionGate(
  overview: Pick<StreamingOverviewResponse, 'sections'>,
  section: PeriodSectionKey
): PeriodSectionGate {
  const sources = overview.sections?.[section];
  if (sources?.locked === 'plan') {
    return 'locked';
  }
  return (sources?.sources ?? []).length === 0 ? 'noSource' : 'open';
}

/** The kinds of source that can give a section, for the e2 cards. */
export function periodSectionGivenBy(
  overview: Pick<StreamingOverviewResponse, 'sections'>,
  section: PeriodSectionKey
): string[] {
  return overview.sections?.[section]?.givenBy ?? [];
}

/** The picked option of the switch, or undefined. */
export function pickedOption(
  sources: readonly StreamingSectionSourceOption[],
  key: string | null
): StreamingSectionSourceOption | undefined {
  return key === null ? undefined : sources.find((option) => option.key === key);
}

/** A share in basis points as a percent with one decimal: 5240 → "52.4%". */
export function formatShareBp(intl: IntlShape, shareBp: number): string {
  return intl.formatNumber(shareBp / 10000, { style: 'percent', minimumFractionDigits: 1, maximumFractionDigits: 1 });
}

/** The band with the largest share: it gets the brand gradient (design a1). Null for no band. */
export function topAgeBand(age: readonly StreamingAudienceAge[]): string | null {
  let top: StreamingAudienceAge | null = null;
  for (const band of age) {
    if (top === null || band.shareBp > top.shareBp) {
      top = band;
    }
  }
  return top?.band ?? null;
}

/** The three tiles of Play sources (design b1): "Listeners and you" adds `you` and `listeners`. */
export type PlaySourceTileKey = 'listenersAndYou' | 'store' | 'other';

export interface PlaySourceTile {
  key: PlaySourceTileKey;
  streams: number;
  /** Null when no row of the tile has a previous number. */
  previousStreams: number | null;
  /** 0 to 1 of all the streams of the rows. */
  share: number;
}

function tileOf(group: StreamingPlaySourceRow['group']): PlaySourceTileKey {
  if (group === 'you' || group === 'listeners') {
    return 'listenersAndYou';
  }
  return group;
}

export function playSourceTiles(rows: readonly StreamingPlaySourceRow[]): PlaySourceTile[] {
  const keys: PlaySourceTileKey[] = ['listenersAndYou', 'store', 'other'];
  const total = rows.reduce((sum, row) => sum + row.streams, 0);
  return keys.map((key) => {
    const own = rows.filter((row) => tileOf(row.group) === key);
    const streams = own.reduce((sum, row) => sum + row.streams, 0);
    const previous = own.filter((row) => row.previousStreams !== null);
    return {
      key,
      streams,
      previousStreams:
        previous.length === 0 ? null : previous.reduce((sum, row) => sum + (row.previousStreams ?? 0), 0),
      share: total > 0 ? streams / total : 0,
    };
  });
}

/** One bar of the month bars: the parts in the order of the tiles, bottom first. */
export interface StackedMonthBar {
  month: string;
  total: number;
  /** 0 to 1 of the largest month. */
  height: number;
  parts: Array<{ key: PlaySourceTileKey; value: number; share: number }>;
}

export function playSourceMonthBars(byMonth: readonly StreamingPlaySourceMonth[]): StackedMonthBar[] {
  const totals = byMonth.map((month) => month.you + month.listeners + month.store + month.other);
  const top = Math.max(0, ...totals);
  return byMonth.map((month, index) => {
    const total = totals[index] ?? 0;
    const values: Array<{ key: PlaySourceTileKey; value: number }> = [
      { key: 'listenersAndYou', value: month.you + month.listeners },
      { key: 'store', value: month.store },
      { key: 'other', value: month.other },
    ];
    return {
      month: month.month,
      total,
      height: top > 0 ? total / top : 0,
      parts: values.map((part) => ({ ...part, share: total > 0 ? part.value / total : 0 })),
    };
  });
}

export const UGC_NUMBER_KEYS: ReadonlyArray<keyof StreamingUgcNumbers> = [
  'creations',
  'views',
  'likes',
  'shares',
  'comments',
  'favourites',
];

/**
 * True when the source gives at least one short-video number, also a 0 (owner rule 2026-10-18: a 0
 * is a number that the source saw). False only when every number is missing: then the screen shows
 * the "No videos" card.
 */
export function hasUgcNumbers(numbers: StreamingUgcNumbers): boolean {
  return UGC_NUMBER_KEYS.some((key) => numbers[key] !== null && numbers[key] !== undefined);
}

export type UgcDayMetric = 'creations' | 'views' | 'likes';

/** The platforms of the day bars, bottom first. */
export const UGC_DAY_PLATFORMS: readonly StreamingUgcPlatform[] = ['tiktok', 'meta', 'youtubeShorts', 'douyin'];

/** One part of a day bar. `height` is null when the platform gives no number that day: no bar. */
export interface UgcDayPart {
  platform: StreamingUgcPlatform;
  value: number | null;
  /** 0 to 100 of the largest day, or null for a missing number. A 0 is a real zero bar. */
  height: number | null;
}

export interface UgcDayBar {
  date: string;
  parts: UgcDayPart[];
}

/**
 * The day bars of Short video (design streaming-sections c1), split by platform. A missing number
 * is not a zero: its part has no bar. A 0 is a real zero bar.
 */
export function ugcDayBars(series: readonly StreamingUgcPoint[], metric: UgcDayMetric): UgcDayBar[] {
  const byDate = new Map<string, Map<StreamingUgcPlatform, number>>();
  for (const point of series) {
    const day = byDate.get(point.date) ?? new Map<StreamingUgcPlatform, number>();
    const value = point[metric];
    if (value !== null && value !== undefined) {
      day.set(point.platform, (day.get(point.platform) ?? 0) + value);
    }
    byDate.set(point.date, day);
  }
  const sorted = [...byDate.entries()].sort(([left], [right]) => left.localeCompare(right));
  const top = Math.max(0, ...sorted.map(([, day]) => [...day.values()].reduce((sum, value) => sum + value, 0)));
  return sorted.map(([date, day]) => ({
    date,
    parts: UGC_DAY_PLATFORMS.map((platform) => {
      const value = day.get(platform);
      if (value === undefined) {
        return { platform, value: null, height: null };
      }
      return { platform, value, height: top > 0 ? (value / top) * 100 : 0 };
    }),
  }));
}

/** The tiles of Accelerator (design d1). */
export interface AcceleratorSummary {
  acceleratorStreams: number;
  /** Null when a month of the period has no overall number: the sum is not known. */
  overallStreams: number | null;
  /** 0 to 1, or null with no stream or no overall number. */
  share: number | null;
  newest: StreamingAcceleratorMonth | null;
  /** The month before the newest, or null. */
  beforeNewest: StreamingAcceleratorMonth | null;
  /** The first month with Accelerator streams, or null. */
  firstMonth: string | null;
}

export function acceleratorSummary(byMonth: readonly StreamingAcceleratorMonth[]): AcceleratorSummary {
  const sorted = [...byMonth].sort((left, right) => left.month.localeCompare(right.month));
  const acceleratorStreams = sorted.reduce((sum, month) => sum + month.acceleratorStreams, 0);
  const missingOverall = sorted.length === 0 || sorted.some((month) => month.overallStreams === null);
  const overallStreams = missingOverall ? null : sorted.reduce((sum, month) => sum + (month.overallStreams ?? 0), 0);
  return {
    acceleratorStreams,
    overallStreams,
    share: overallStreams !== null && overallStreams > 0 ? acceleratorStreams / overallStreams : null,
    newest: sorted[sorted.length - 1] ?? null,
    beforeNewest: sorted[sorted.length - 2] ?? null,
    firstMonth: sorted.find((month) => month.acceleratorStreams > 0)?.month ?? null,
  };
}

/** One bar of the Accelerator months: the Accelerator part on top of the other streams. */
export interface AcceleratorBar {
  month: string;
  /** 0 to 1 of the largest month, or null when TuneCore gave no overall number: no bar. */
  height: number | null;
  /** 0 to 1 of the month: the Accelerator part. Null with no overall number: a dash. */
  share: number | null;
}

export function acceleratorBars(byMonth: readonly StreamingAcceleratorMonth[]): AcceleratorBar[] {
  const sorted = [...byMonth].sort((left, right) => left.month.localeCompare(right.month));
  const top = Math.max(0, ...sorted.map((month) => month.overallStreams ?? 0));
  return sorted.map((month) => {
    const overall = month.overallStreams;
    if (overall === null) {
      return { month: month.month, height: null, share: null };
    }
    return {
      month: month.month,
      height: top > 0 ? overall / top : 0,
      share: overall > 0 ? Math.min(1, month.acceleratorStreams / overall) : 0,
    };
  });
}

/**
 * What Accelerator shows: `noData` when TuneCore sent no month at all (no rows; never "TuneCore
 * reports 0"), `off` when every month of the last 12 is 0 (owner decision O6), else the numbers.
 */
export type AcceleratorView = 'noData' | 'off' | 'numbers';

export function acceleratorView(answer: { byMonth: readonly unknown[]; allZero: boolean }): AcceleratorView {
  if (answer.byMonth.length === 0) {
    return 'noData';
  }
  return answer.allZero ? 'off' : 'numbers';
}
